import { useEffect, useState } from 'react';
import { api, User, Organization, Project, ProjectWithPermission } from './api';
import {
  useHashState,
  ErrorBoundary,
  useVersionWatcher,
  useBeforeUnloadWhileBusy,
  confirmLeaveWhileBusy,
  UnauthorizedError,
} from '../../shared/src';
import { LoginPage, Sidebar, Page } from './components';
import { CreateProjectModal } from './components/modals';
import { isAllowedExternalUiUrl } from './lib/external-ui-url';
import {
  DashboardPage,
  ProfilePage,
  AdminPage,
  UploadsPage,
  SetPasswordPage,
  ForgotPasswordPage,
  ResetPasswordPage,
  ProjectSelectionPage,
} from './pages';
import './App.css';

type AuthPage = 'login' | 'set-password' | 'forgot-password' | 'reset-password';
type AdminTab = 'people' | 'permissions' | 'organization' | 'audit';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [authPage, setAuthPage] = useState<AuthPage>('login');
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [error, setError] = useState('');
  // Project whose external-UI handoff is in flight — the one-time-code mint is
  // an async API call BEFORE navigation, so the tile shows a spinner meanwhile.
  const [handoffProjectId, setHandoffProjectId] = useState<string | null>(null);

  // Leaving via location.assign parks this page in the browser's back/forward
  // cache with the spinner state frozen in; pressing Back restores it as-is
  // (React never re-mounts, so the tile spins forever). pageshow fires on every
  // restore — clear the in-flight marker so the tile is clickable again.
  useEffect(() => {
    const clearHandoff = () => setHandoffProjectId(null);
    window.addEventListener('pageshow', clearHandoff);
    return () => window.removeEventListener('pageshow', clearHandoff);
  }, []);

  // One-time notice surfaced on the login screen after a session-expiry redirect.
  const [sessionNotice] = useState<string | null>(() => {
    if (typeof sessionStorage === 'undefined') return null;
    const reason = sessionStorage.getItem('post_logout_reason');
    if (reason) sessionStorage.removeItem('post_logout_reason');
    return reason === 'session_expired'
      ? 'Your session expired. Please sign in again.'
      : null;
  });

  // CI/CD robustness: silently refresh idle clients to a new deploy (deferred while a busy
  // operation — upload or bulk delete — runs), and warn before leaving the page mid-operation.
  // Active regardless of auth state so even the login screen self-updates.
  useVersionWatcher();
  useBeforeUnloadWhileBusy();

  // Projects accessible to the current user
  const [viewerProjects, setViewerProjects] = useState<ProjectWithPermission[]>([]);
  const [ownerProjects, setOwnerProjects] = useState<ProjectWithPermission[]>([]);

  const [showCreateProjectModal, setShowCreateProjectModal] = useState(false);

  // Hash-based navigation state. `onBeforePop` guards the browser back/forward buttons,
  // which change the hash via popstate without going through the click handlers or
  // beforeunload — so without this, Back could silently abandon an in-progress upload.
  const [hashState, setHashState] = useHashState(
    {
      page: 'dashboard' as string,
      tab: null as string | null,
      subtab: null as string | null,
      folder: null as string | null,
      file: null as string | null,
      project: null as string | null,
    },
    { onBeforePop: () => confirmLeaveWhileBusy() },
  );

  // Type-safe accessors
  const currentPage = (hashState.page || 'dashboard') as Page;
  const adminTab = (hashState.tab || 'people') as AdminTab;
  const adminSubtab = (hashState.subtab || 'users') as string;
  const currentProjectSlug = hashState.project;

  // Derive whether user has any owner-level project access
  const hasOwnerProject = ownerProjects.length > 0;

  // Find the current project by slug
  const currentProject = currentProjectSlug
    ? viewerProjects.find((p) => p.slug === currentProjectSlug) || null
    : null;

  useEffect(() => {
    // Check for special auth routes
    const path = window.location.pathname;

    if (path === '/forgot-password') {
      setAuthPage('forgot-password');
      setLoading(false);
      return;
    }

    if (path === '/reset-password') {
      const params = new URLSearchParams(window.location.search);
      const token = params.get('token');
      if (token) {
        setAuthToken(token);
        setAuthPage('reset-password');
      }
      setLoading(false);
      return;
    }

    if (path === '/set-password') {
      const params = new URLSearchParams(window.location.search);
      const token = params.get('token');
      if (token) {
        setAuthToken(token);
        setAuthPage('set-password');
      }
      setLoading(false);
      return;
    }

    // Check for token in URL hash (after SSO redirect)
    const hash = window.location.hash;
    if (hash.includes('token=')) {
      const token = hash.split('token=')[1]?.split('&')[0];
      if (token) {
        api.setToken(token);
        // Clear the hash completely after extracting token
        window.history.replaceState(null, '', window.location.pathname);
        setIsAuthenticated(true);
        loadUserData();
        return;
      }
    }

    if (api.isAuthenticated()) {
      setIsAuthenticated(true);
      loadUserData();
    } else {
      setLoading(false);
    }
  }, []);

  const loadUserData = async () => {
    // Show the spinner for the duration of the load. Important on the
    // post-login path: `loading` is already false from bootstrap, so
    // without this the shell flashes empty before the data arrives.
    setLoading(true);
    try {
      // Load everything the sidebar/dashboard depend on before flipping
      // loading=false — otherwise the shell renders with placeholders for
      // user/org/projects and visibly re-paints as each request resolves.
      const [userData, orgData, viewer, owner] = await Promise.all([
        api.getMe(),
        api.getOrganization(),
        api.getMyProjects('viewer').catch(() => [] as ProjectWithPermission[]),
        api.getMyProjects('owner').catch(() => [] as ProjectWithPermission[]),
      ]);
      setUser(userData);
      setOrganization(orgData);
      setViewerProjects(viewer);
      setOwnerProjects(owner);
      setError('');
    } catch (err: unknown) {
      const error = err as Error;
      setError(error.message);
      if (error instanceof UnauthorizedError) {
        setIsAuthenticated(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = () => {
    api.initiateLogin();
  };

  const handlePasswordLogin = (token: string) => {
    api.setToken(token);
    setIsAuthenticated(true);
    loadUserData();
  };

  const handleSetPasswordSuccess = (token: string) => {
    api.setToken(token);
    window.history.replaceState(null, '', '/');
    setIsAuthenticated(true);
    loadUserData();
  };

  const handleResetPasswordSuccess = () => {
    window.history.replaceState(null, '', '/');
    setAuthPage('login');
  };

  const handleBackToLogin = () => {
    window.history.replaceState(null, '', '/');
    setAuthPage('login');
  };

  const handleLogout = async () => {
    // Signing out clears auth, so an in-flight operation's API calls would 401 — guard it
    // (the beforeunload guard doesn't cover this in-app action).
    if (!confirmLeaveWhileBusy('An operation is in progress. Signing out will interrupt it. Sign out anyway?')) {
      return;
    }
    await api.logout();
    setIsAuthenticated(false);
    setUser(null);
    setOrganization(null);
    setViewerProjects([]);
    setOwnerProjects([]);
  };

  const handlePageChange = (page: Page) => {
    // Navigating away unmounts the file browser, so guard in-app page switches (sidebar tabs)
    // the same way the beforeunload guard covers a browser reload/close.
    if (!confirmLeaveWhileBusy()) return;
    // Reset tab, project, folder when changing pages
    const tab = page === 'admin' ? 'people' : null;
    const subtab = page === 'admin' ? 'users' : null;
    setHashState({ page, tab, subtab, folder: null, file: null, project: null });
  };

  const handleAdminTabChange = (tab: AdminTab) => {
    const subtab = tab === 'permissions' ? 'users' : null;
    setHashState({ tab, subtab });
  };

  const handleAdminSubtabChange = (subtab: string) => {
    setHashState({ subtab });
  };

  const handleFolderChange = (folder: string | null, file: string | null = null) => {
    setHashState({ folder, file });
  };

  const handleSelectProject = (project: ProjectWithPermission) => {
    setHashState({ project: project.slug, folder: null, file: null });
  };

  const handleSelectDashboardProject = async (project: ProjectWithPermission) => {
    if (project.external_ui_url) {
      // Defence in depth: the DB CHECK on projects.external_ui_url already
      // restricts the value to https://*.fakto.ai, but we gate again at
      // the redirect with an EXACT host allowlist so a misconfigured row
      // can't turn a project tile into token exfiltration. If the URL is
      // unsafe, fall through to the in-app navigation rather than the
      // redirect — the user sees the in-app project page (probably empty
      // for an "external" project) and we log loudly to the console for
      // ops to investigate.
      if (!isAllowedExternalUiUrl(project.external_ui_url)) {
        // eslint-disable-next-line no-console
        console.error(
          'Refusing to redirect to external_ui_url: not in allowlist',
          { project: project.slug, url: project.external_ui_url },
        );
        handleSelectProject(project);
        return;
      }

      // Spinner on the tile while the code mints; the page unloads on
      // navigation, so it only needs clearing on the non-navigating path above.
      setHandoffProjectId(project.id);

      const dest = new URL(project.external_ui_url);
      // Code mode unless a build was explicitly told "token" (the kill switch
      // in deploy.yml) — so local and CI builds hand off as deployed ones do.
      const mode = import.meta.env.VITE_EXTERNAL_UI_HANDOFF_MODE || 'code';

      // Mode "code" (post-rollout): mint a one-time exchange code, redirect
      // with `#code=...`. The receiver redeems it server-to-server, mints
      // its own audience-scoped session, sets an HttpOnly cookie. The full
      // webapp JWT never crosses the origin boundary. See DEFERRED.md
      // "Proper handoff via one-time exchange code".
      if (mode === 'code') {
        try {
          const { code } = await api.requestHandoffCode({
            projectSlug: project.slug,
            externalHost: dest.host.toLowerCase(),
          });
          dest.hash = `code=${code}`;
          window.location.assign(dest.toString());
          return;
        } catch (err) {
          // No fallback to the token link: it would put the user's full
          // webapp JWT in the URL of a host the server just refused (a 403 —
          // the project does not open that app), or of a code-only receiver
          // that cannot use it. A 401 has already sent the user to sign in
          // (the shared API client); anything else is said here, and the tile
          // can be clicked again.
          // eslint-disable-next-line no-console
          console.error('handoff code request failed', err);
          setHandoffProjectId(null);
          setError(`${project.name} could not be opened. Please try again.`);
          return;
        }
      }

      // Legacy token flow (default mode). Hand off auth to the external UI
      // via the URL fragment — the receiver reads `#token=...` on boot and
      // stashes it in its own origin-scoped localStorage. Fragments never
      // travel in HTTP requests so the token doesn't leak through Referer
      // or server logs. Mirrors the existing SSO-return pattern further
      // up in this file.
      const token = api.getToken();
      if (token) {
        dest.hash = `token=${token}`;
      }
      window.location.assign(dest.toString());
      return;
    }
    handleSelectProject(project);
  };

  const handleBackToProjectList = () => {
    // "Back" leaves the file browser (and any running upload/delete) — guard like other nav.
    if (!confirmLeaveWhileBusy()) return;
    setHashState({ project: null, folder: null, file: null });
  };

  const handleCreateProjectSuccess = async (project: Project) => {
    setShowCreateProjectModal(false);
    const [viewer, owner] = await Promise.all([
      api.getMyProjects('viewer').catch(() => [] as ProjectWithPermission[]),
      api.getMyProjects('owner').catch(() => [] as ProjectWithPermission[]),
    ]);
    setViewerProjects(viewer);
    setOwnerProjects(owner);
    setHashState({ project: project.slug, folder: null, file: null });
  };

  if (loading) {
    return (
      <div className="loading-page">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  // Unauthenticated views
  if (!isAuthenticated) {
    if (authPage === 'set-password' && authToken) {
      return (
        <SetPasswordPage token={authToken} onSuccess={handleSetPasswordSuccess} />
      );
    }

    if (authPage === 'reset-password' && authToken) {
      return (
        <ResetPasswordPage token={authToken} onSuccess={handleResetPasswordSuccess} />
      );
    }

    if (authPage === 'forgot-password') {
      return <ForgotPasswordPage onBack={handleBackToLogin} />;
    }

    return (
      <LoginPage
        onLogin={handleLogin}
        onPasswordLogin={handlePasswordLogin}
        notice={sessionNotice}
      />
    );
  }

  // Check if user can see uploads section
  const canSeeUploads = hasOwnerProject || user?.role === 'org_admin';

  // Whether the current user can create new projects. Today the backend gates
  // POST /app/projects on org_admin (lambda/app-api/project-handlers.ts); keep
  // the frontend check in sync.
  const canCreateProject = user?.role === 'org_admin';

  return (
    <ErrorBoundary>
      <div className="app-layout">
        <Sidebar
          user={user}
          organization={organization}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          onLogout={handleLogout}
          hasOwnerProject={hasOwnerProject}
        />
        <main className="main-content">
          {error && <div className="error-banner">{error}</div>}

          {/* Dashboard: project list or project-scoped view */}
          {currentPage === 'dashboard' && !currentProjectSlug && (
            <ProjectSelectionPage
              projects={viewerProjects}
              pageType="dashboard"
              onSelectProject={handleSelectDashboardProject}
              canCreateProject={canCreateProject}
              onCreateProject={() => setShowCreateProjectModal(true)}
              busyProjectId={handoffProjectId}
            />
          )}
          {currentPage === 'dashboard' && currentProjectSlug && currentProject && (
            <DashboardPage
              user={user}
              projectSlug={currentProjectSlug}
              projectName={currentProject.name}
              onBack={handleBackToProjectList}
            />
          )}

          {currentPage === 'profile' && (
            <ProfilePage user={user} organization={organization} onUpdate={loadUserData} />
          )}

          {/* Uploads: project list or project-scoped file browser */}
          {currentPage === 'uploads' && canSeeUploads && !currentProjectSlug && (
            <ProjectSelectionPage
              projects={ownerProjects}
              pageType="uploads"
              onSelectProject={handleSelectProject}
              canCreateProject={canCreateProject}
              onCreateProject={() => setShowCreateProjectModal(true)}
            />
          )}
          {currentPage === 'uploads' && canSeeUploads && currentProjectSlug && currentProject && (
            <UploadsPage
              currentFolderId={hashState.folder}
              onFolderChange={handleFolderChange}
              rootFolderId={currentProject.root_folder_id}
              projectId={currentProject.id}
              projectName={currentProject.name}
              onBack={handleBackToProjectList}
            />
          )}

          {currentPage === 'admin' && user?.role === 'org_admin' && (
            <AdminPage
              organization={organization}
              currentUserId={user?.id}
              activeTab={adminTab}
              activeSubtab={adminSubtab}
              onTabChange={handleAdminTabChange}
              onSubtabChange={handleAdminSubtabChange}
            />
          )}
        </main>

        {showCreateProjectModal && canCreateProject && (
          <CreateProjectModal
            onClose={() => setShowCreateProjectModal(false)}
            onSuccess={handleCreateProjectSuccess}
          />
        )}
      </div>
    </ErrorBoundary>
  );
}

export default App;
